/* eslint-disable arrow-body-style */
/* eslint-disable curly */
import { Component, Input, ChangeDetectorRef, ElementRef, ViewChild, Output, EventEmitter, AfterViewInit, OnInit, OnDestroy } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { first, map } from "rxjs/operators";
import { ProductSource } from "src/app/data-models/wl-account-setup/wl-config-data.model";
import { WhiteLabelConfigurationService } from "src/app/services/white-labels/white-label-configuration.service";
import { Exceptions } from "src/app/utils/exceptions";
import { setupFullscreenPanelObserver } from "src/app/utils/intiaro-fullscreen-observer";
import { injectNonVisualThumbnail } from "src/app/utils/intiaro-non-visual-thumbnail";

declare var IntiaroAPI: any;

@Component({
  selector: "app-product-config-configurator360",
  template: "<div #element360 class=\"product_config_configurator360_container\"></div>",
  styleUrls: ["./product-config-configurator360.component.scss"]
})
export class ProductConfigConfigurator360Component implements AfterViewInit, OnInit, OnDestroy {

  // v3.0: isAngleAllowed() normalizes angles via getPositiveAngles() before comparing against allowedAngles.
  // Negative angles must be expressed as their positive equivalents (e.g. -40 → 320, -10 → 350).
  public static readonly ALLOWED_ANGLES = "[320,330,340,350,0,10,20,30,40]";
  @Output() configurator360ReadyEvent: EventEmitter<boolean> = new EventEmitter();   
  @Input() brandId!: string;
  @Input() configurationId!: string;
  @Input() showMenu:boolean = true;
  @Input() context?: string;
  @Input() isVisual: boolean = true;
  @Input() thumbnailUrl?: string;
  
  @ViewChild("element360", { read: ElementRef })
  private element360!: ElementRef;
  hiddenInactiveChoices: boolean = false;
  wlSlug: string = "";

  public static readonly defaultConfiguratorId:string = "product-configurator";
  get defaultConfiguratorId():string {
    return ProductConfigConfigurator360Component.defaultConfiguratorId;
  }
  
  public isConfigurator360Loaded:Subject<boolean> = new Subject();
  public intiaroApiInstance: any = null;
  private fullscreenPanelObserver: MutationObserver | null = null;
  private productSourceSubscription?: Subscription;

  constructor(
    public changeDetector: ChangeDetectorRef,
    private wlConfigurationService: WhiteLabelConfigurationService
  ) {}

  ngOnInit(): void {
    this.wlConfigurationService.whiteLabelSetupConfiguration.subscribe((wlConfig) => {
      this.hiddenInactiveChoices = wlConfig.configuratorInactiveChoicesVisible ?? false;
      this.wlSlug = wlConfig.slug;
    });
    this.changeDetector.detectChanges();
  }

  ngAfterViewInit(): void {
    // The product source picks the configurator's data, so the configurator waits for the white label configuration.
    this.productSourceSubscription = this.wlConfigurationService.whiteLabelSetupConfiguration.pipe(
      map((wlConfig) => wlConfig?.productSource),
      first((productSource): productSource is ProductSource => productSource !== undefined)
    ).subscribe((productSource) => this.createIntiaroInstance(productSource));
  }

  private async createIntiaroInstance(productSource: ProductSource): Promise<void> {
    this.changeDetector.detectChanges();
    this.element360.nativeElement.id = this.defaultConfiguratorId;

    this.intiaroApiInstance = new IntiaroAPI({
      productId: this.configurationId,
      brandId: this.brandId,
      domainTemplate: productSource,
      environment: productSource,
      ...(this.context ? { context: this.context } : {}),
    });

    const viewerConfiguration: any = {
      siloShotButton: false,
      ...(!this.isVisual ? { navigationButtons: false, measurementsButton: false, siloShotButton: false, fullscreenButton: false } : {}),
    };

    if (Exceptions.isWindowDraperyProduct(this.wlSlug, this.configurationId)) {
      viewerConfiguration.allowedAngles = JSON.parse(ProductConfigConfigurator360Component.ALLOWED_ANGLES);
      viewerConfiguration.arButton = false;
    }

    if (this.showMenu) {
      await this.intiaroApiInstance.createConfigurator({
        parentElement: this.element360.nativeElement,
        viewerConfiguration,
        menuConfiguration: {
          inactiveChoicesBehaviour: this.hiddenInactiveChoices ? "hide" : "gray-out",
          // v3.0 (FFY-4550): hide attributes that offer only one choice. Bundles without
          // support for this option ignore the key, so it is safe to send before the release.
          singleChoiceAttributes: "hidden",
        },
      });
      this.fullscreenPanelObserver = setupFullscreenPanelObserver(this.element360.nativeElement);
    } else {
      await this.intiaroApiInstance.createViewer({
        parentElement: this.element360.nativeElement,
        viewerConfiguration,
      });
    }

    if (!this.isVisual && this.thumbnailUrl) {
      injectNonVisualThumbnail(this.element360.nativeElement, this.thumbnailUrl);
    }

    this.intiaro360Ready();
  }

  ngOnDestroy(): void {
    this.productSourceSubscription?.unsubscribe();
    this.fullscreenPanelObserver?.disconnect();
  }

  private intiaro360Ready(): void {
    this.isConfigurator360Loaded.next(true);
    this.configurator360ReadyEvent.emit(true);
  }
}
